import {ReactNode} from 'react';

interface ProjectOverviewHeaderProps {
  title: string;
  description?: string;
  status?: 'active' | 'inactive' | 'completed' | 'draft';
  showStatus: boolean;
  containerClassName?: string;
  children?: ReactNode;
  /** Optional element rendered immediately after the title (e.g. an info tooltip). */
  titleAdornment?: ReactNode;
}

export default function ProjectHeader({
  title,
  description,
  status,
  showStatus,
  containerClassName,
  children,
  titleAdornment,
}: ProjectOverviewHeaderProps) {
  const getStatusConfig = (status: 'active' | 'inactive' | 'completed' | 'draft') => {
    switch (status) {
      case 'active':
        return {
          dotColor: 'bg-green-500',
          textColor: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Active',
        };
      case 'inactive':
        return {
          dotColor: 'bg-gray-400',
          textColor: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Inactive',
        };
      case 'completed':
        return {
          dotColor: 'bg-blue-500',
          textColor: 'text-blue-700',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          label: 'Completed',
        };
      case 'draft':
        return {
          dotColor: 'bg-yellow-500',
          textColor: 'text-yellow-700',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Draft',
        };
      default:
        return {
          dotColor: 'bg-gray-400',
          textColor: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Unknown',
        };
    }
  };

  const statusConfig = getStatusConfig(status);

  return (
    <div
      className={`flex items-start justify-between ${containerClassName || 'mb-7'}`}
    >
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-bold capitalize text-foreground">{title}</h1>
          {titleAdornment}
          {showStatus && (
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${statusConfig.bgColor} ${statusConfig.borderColor} ${statusConfig.textColor} gap-1.5`}
            >
              <div
                className={`size-1.5 rounded-full ${statusConfig.dotColor}`}
              />
              {statusConfig.label}
            </span>
          )}
        </div>
        {description?.trim() ? (
          <p className="text-muted-foreground">{description}</p>
        ) : null}
      </div>

      {children}
    </div>
  );
}
